import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useResource } from '../../lib/useResource.js';
import { post, put } from '../../lib/api.js';
import { formatBRL, formatDateTime } from '../../lib/format.js';
import {
  Alert,
  Badge,
  Card,
  Empty,
  ErrorBox,
  Field,
  Modal,
  PageHeader,
  useSubmit,
  useToast,
} from '../../components/ui.js';
import { useSession } from '../../lib/session.js';
import { Tabela } from '../../components/Tabela.js';

export function SettingsLayout() {
  const { can } = useSession();
  return (
    <>
      <PageHeader title="Configurações" />
      <nav className="tabs" aria-label="Seções de configuração">
        <NavLink to="/configuracoes" end>
          Loja e marca
        </NavLink>
        <NavLink to="/configuracoes/ia">Regras da IA</NavLink>
        <NavLink to="/configuracoes/descontos">Descontos</NavLink>
        <NavLink to="/configuracoes/notificacoes">Notificações</NavLink>
        <NavLink to="/configuracoes/mensagens-prontas">Mensagens prontas</NavLink>
        <NavLink to="/configuracoes/equipe">Equipe</NavLink>
        <NavLink to="/configuracoes/whatsapp">WhatsApp</NavLink>
        {can('costs.read') ? (
          <NavLink to="/configuracoes/custos">Custos e limites</NavLink>
        ) : null}
        {can('company.danger_zone') ? (
          <NavLink to="/configuracoes/retencao">Retenção de dados</NavLink>
        ) : null}
        {can('system.diagnose') ? (
          <NavLink to="/configuracoes/sistema">Sistema</NavLink>
        ) : null}
      </nav>
      <Outlet />
    </>
  );
}

// ------------------------------------------------------------ loja e marca

interface ContrastCheck {
  id: string;
  label: string;
  foreground: string;
  background: string;
  required: number;
  ratio: number;
  passes: boolean;
}

const TOKENS: { key: string; label: string; hint?: string }[] = [
  { key: 'primary', label: 'Amarelo da marca' },
  { key: 'primaryBorder', label: 'Contorno do botão', hint: 'Dá o limite visível ao botão amarelo.' },
  { key: 'primaryStrong', label: 'Texto na cor da marca' },
  { key: 'ink', label: 'Texto principal' },
  { key: 'muted', label: 'Texto secundário' },
  { key: 'background', label: 'Fundo da página' },
  { key: 'surface', label: 'Cartões e tabelas' },
  { key: 'accent', label: 'Destaque' },
  { key: 'accentBorder', label: 'Contorno do destaque' },
  { key: 'accentStrong', label: 'Texto do destaque' },
  { key: 'onPrimary', label: 'Texto sobre o amarelo', hint: 'Precisa ser escuro. Branco reprova.' },
];

export function LojaEMarca() {
  const { store, reloadStore, can } = useSession();
  const toast = useToast();
  const [brand, setBrand] = useState<Record<string, string>>({});
  const [checks, setChecks] = useState<ContrastCheck[]>([]);
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    if (store?.brand) {
      setBrand(store.brand as Record<string, string>);
      setLogoUrl((store.brand.logoUrl as string) ?? '');
    }
  }, [store]);

  useEffect(() => {
    if (Object.keys(brand).length === 0) return;
    const timer = setTimeout(() => {
      post<{ checks: ContrastCheck[] }>('/api/settings/brand/preview', brand)
        .then((r) => setChecks(r.checks))
        .catch(() => setChecks([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [brand]);

  const reprovados = checks.filter((c) => !c.passes);

  const { submit, saving, error } = useSubmit(async () => {
    await put('/api/settings/brand', { ...brand, logoUrl: logoUrl || null });
    await reloadStore();
    toast('Marca atualizada.');
  });

  const info = useSubmit(async () => {
    await put('/api/settings/store-info', {
      storeInfo: { ...(store?.storeInfo ?? {}), unconfirmedFields: [] },
      confirmed: true,
    });
    await reloadStore();
    toast('Dados da loja confirmados.');
  });

  if (!can('company.settings.write')) {
    return (
      <Card>
        <Empty title="Você não tem permissão para alterar as configurações." />
      </Card>
    );
  }

  return (
    <>
      <Card title="Dados da loja">
        <ErrorBox error={info.error} />
        {store && !store.storeInfoConfirmed ? (
          <Alert tone="warning" title="Rascunho não confirmado">
            Estes dados foram observados no perfil público e não devem ser usados em mensagem até a
            confirmação do proprietário.
          </Alert>
        ) : null}
        <dl style={{ margin: 0 }}>
          {[
            ['Endereço', store?.storeInfo.address],
            ['Cidade', store?.storeInfo.city ? `${store.storeInfo.city}/${store.storeInfo.state ?? ''}` : null],
            ['Telefone', store?.storeInfo.phone],
            ['Instagram', store?.storeInfo.instagram],
            ['Horários', store?.storeInfo.openingHours],
          ].map(([label, value]) => (
            <div key={String(label)} style={{ display: 'flex', gap: 10, padding: '6px 0' }}>
              <dt style={{ minWidth: 110, fontWeight: 600 }}>{label}</dt>
              <dd style={{ margin: 0 }}>
                {value || <span className="muted">não informado</span>}
                {value && !store?.storeInfoConfirmed ? (
                  <span className="unconfirmed">não confirmado</span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
        {store && !store.storeInfoConfirmed ? (
          <button type="button" className="primary" disabled={info.saving} onClick={() => void info.submit()}>
            {info.saving ? 'Confirmando…' : 'Confirmar estes dados'}
          </button>
        ) : (
          <Badge tone="ok">confirmado pelo proprietário</Badge>
        )}
      </Card>

      <Card title="Marca" description="As cores valem no painel inteiro. Combinação reprovada não é salva.">
        <form onSubmit={submit}>
          <ErrorBox error={error} />

          <Field label="Logo da loja" hint="Envie a URL do arquivo PNG transparente ou SVG em alta resolução.">
            <input type="text" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…" />
          </Field>
          {!logoUrl ? (
            <Alert tone="info" title="Logo provisória em uso">
              Enquanto o arquivo original não for enviado, o painel mostra o monograma da loja. A
              miniatura do perfil público não é um arquivo vetorial oficial.
            </Alert>
          ) : null}

          <div className="field-row">
            {TOKENS.map((token) => (
              <Field key={token.key} label={token.label} hint={token.hint}>
                <div className="row" style={{ flexWrap: 'nowrap' }}>
                  <input
                    type="color"
                    value={brand[token.key] ?? '#000000'}
                    onChange={(e) => setBrand({ ...brand, [token.key]: e.target.value })}
                    style={{ width: 52 }}
                    aria-label={token.label}
                  />
                  <input
                    type="text"
                    value={brand[token.key] ?? ''}
                    onChange={(e) => setBrand({ ...brand, [token.key]: e.target.value })}
                    className="mono"
                  />
                </div>
              </Field>
            ))}
          </div>

          <h3 style={{ marginTop: 18, marginBottom: 8 }}>Contraste</h3>
          {reprovados.length > 0 ? (
            <Alert tone="error" title="Esta combinação não pode ser salva">
              Ajuste as cores reprovadas abaixo.
            </Alert>
          ) : (
            <Alert tone="info" title="Todas as combinações passam no mínimo exigido" />
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Combinação</th>
                  <th className="num">Medido</th>
                  <th className="num">Mínimo</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((check) => (
                  <tr key={check.id}>
                    <td>
                      {check.label}
                      <span
                        style={{
                          display: 'inline-block',
                          marginLeft: 8,
                          padding: '1px 8px',
                          borderRadius: 4,
                          background: check.background,
                          color: check.foreground,
                          border: '1px solid var(--gl-border)',
                          fontSize: 12,
                        }}
                      >
                        exemplo
                      </span>
                    </td>
                    <td className="num">{check.ratio.toFixed(2)}:1</td>
                    <td className="num">{check.required}:1</td>
                    <td>
                      <Badge tone={check.passes ? 'ok' : 'critical'}>
                        {check.passes ? 'aprovado' : 'reprovado'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ marginTop: 16 }}>
            <button type="submit" className="primary" disabled={saving || reprovados.length > 0}>
              {saving ? 'Salvando…' : 'Salvar marca'}
            </button>
          </div>
        </form>
      </Card>
    </>
  );
}

// ------------------------------------------------------------------ descontos

export function Descontos() {
  const { store, reloadStore, can } = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const habilitado = store?.settings.discountsEnabled ?? false;

  const { submit, saving, error } = useSubmit(async () => {
    await put('/api/settings/discounts-enabled', { enabled: !habilitado, reason });
    await reloadStore();
    setReason('');
    toast(habilitado ? 'Negociação desativada.' : 'Negociação ativada.');
  });

  return (
    <>
      <Card title="Negociação de desconto">
        <ErrorBox error={error} />
        <p>
          Situação atual:{' '}
          <Badge tone={habilitado ? 'attention' : 'ok'}>
            {habilitado ? 'negociação ativada' : 'desconto desabilitado'}
          </Badge>
        </p>
        <p className="small muted">
          O desconto vem desabilitado por padrão. Com a negociação ativada, cada item ou categoria
          ainda precisa de uma política própria: o limite nunca é aberto por insistência do cliente,
          e a frase "o dono autorizou" não autoriza nada.
        </p>
        {can('discounts.policy.write') ? (
          <form onSubmit={submit}>
            <Field label="Motivo da mudança" required hint="Fica registrado na auditoria.">
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} />
            </Field>
            <button type="submit" disabled={saving || reason.trim().length < 3}>
              {saving ? 'Salvando…' : habilitado ? 'Desativar negociação' : 'Ativar negociação'}
            </button>
          </form>
        ) : (
          <Alert tone="info">Somente proprietário ou administrador altera esta configuração.</Alert>
        )}
      </Card>

      {habilitado ? <PoliticasPorItem /> : null}
    </>
  );
}

interface Item {
  id: string;
  name: string;
  sku: string;
}

function PoliticasPorItem() {
  const toast = useToast();
  const itens = useResource<{ items: Item[] }>('/api/catalog/search?limit=100');
  const [selecionado, setSelecionado] = useState('');
  const explicacao = useResource<{ sentence: string; maxDiscountCents: number; conditions: string[] }>(
    selecionado ? `/api/discount-policies/explain/${selecionado}` : null,
  );
  const [editando, setEditando] = useState(false);

  return (
    <Card title="Limites por item" description="A tela mostra em linguagem simples o que a IA pode conceder.">
      <Field label="Item">
        <select value={selecionado} onChange={(e) => setSelecionado(e.target.value)}>
          <option value="">Escolha um item</option>
          {(itens.data?.items ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>

      {selecionado && explicacao.data ? (
        <>
          <Alert tone="info" title="O que está autorizado hoje">
            {explicacao.data.sentence}
            {explicacao.data.conditions.length ? (
              <ul style={{ margin: '8px 0 0 18px' }}>
                {explicacao.data.conditions.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            ) : null}
          </Alert>
          <button type="button" onClick={() => setEditando(true)}>
            Definir limites deste item
          </button>
        </>
      ) : null}

      {editando && selecionado ? (
        <EditarPolitica
          itemId={selecionado}
          onClose={() => setEditando(false)}
          onSaved={() => {
            setEditando(false);
            explicacao.reload();
            toast('Política salva.');
          }}
        />
      ) : null}
    </Card>
  );
}

function EditarPolitica({
  itemId,
  onClose,
  onSaved,
}: {
  itemId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [enabled, setEnabled] = useState(true);
  const [strategy, setStrategy] = useState<'fixed_offer' | 'negotiate'>('fixed_offer');
  const [maxPercent, setMaxPercent] = useState('5');
  const [minPrice, setMinPrice] = useState('');
  const [initialOffer, setInitialOffer] = useState('');
  const [pix, setPix] = useState(false);

  const { submit, saving, error } = useSubmit(async () => {
    await put('/api/discount-policies', {
      scope: 'item',
      itemId,
      enabled,
      strategy,
      maxPercentBps: maxPercent ? Math.round(Number(maxPercent.replace(',', '.')) * 100) : null,
      initialOfferBps: initialOffer ? Math.round(Number(initialOffer.replace(',', '.')) * 100) : null,
      minPriceCents: minPrice ? Math.round(Number(minPrice.replace(',', '.')) * 100) : null,
      paymentMethods: pix ? ['pix'] : [],
      reason: 'definido no painel',
    });
    onSaved();
  });

  return (
    <Modal
      title="Limites de desconto do item"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-politica" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <form id="form-politica" onSubmit={submit}>
        <ErrorBox error={error} />
        <label className="checkbox">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Permitir desconto neste item
        </label>
        <Field label="Estratégia">
          <select value={strategy} onChange={(e) => setStrategy(e.target.value as typeof strategy)}>
            <option value="fixed_offer">Oferta fixa única</option>
            <option value="negotiate">Negociar até o máximo</option>
          </select>
        </Field>
        <div className="field-row">
          <Field label="Máximo (%)">
            <input type="text" value={maxPercent} onChange={(e) => setMaxPercent(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Oferta inicial (%)" hint="Deixe em branco para oferecer o máximo.">
            <input type="text" value={initialOffer} onChange={(e) => setInitialOffer(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Preço mínimo (R$)" hint="Preço unitário que nunca é rompido.">
            <input type="text" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} inputMode="decimal" />
          </Field>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={pix} onChange={(e) => setPix(e.target.checked)} />
          Valer somente no Pix
        </label>
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------------- equipe

interface Member {
  id: string;
  name: string;
  email: string;
  status: string;
  roleKey: string;
  lastLoginAt: string | null;
}

const PAPEL: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Administrador',
  attendant: 'Atendente',
  viewer: 'Somente leitura',
};

export function Equipe() {
  const { can, me } = useSession();
  const toast = useToast();
  const team = useResource<{ members: Member[] }>(can('users.read') ? '/api/team' : null);
  const [novo, setNovo] = useState(false);

  if (!can('users.read')) {
    return (
      <Card>
        <Empty title="Você não tem permissão para ver a equipe." />
      </Card>
    );
  }

  return (
    <>
      <Card
        title="Equipe"
        description="Atendente não vê custo de compra nem altera preço e política."
        actions={
          can('users.write') ? (
            <button type="button" className="small primary" onClick={() => setNovo(true)}>
              Novo usuário
            </button>
          ) : null
        }
        tight
      >
        <ErrorBox error={team.error} />
        <Tabela
          linhas={team.data?.members ?? []}
          chave={(m) => m.id}
          vazio="Nenhum usuário cadastrado."
          colunas={[
            { chave: 'nome', rotulo: 'Nome', tipo: 'principal', render: (m) => m.name },
            { chave: 'email', rotulo: 'E-mail', render: (m) => <span className="small">{m.email}</span> },
            {
              chave: 'papel',
              rotulo: 'Papel',
              render: (m) =>
                can('users.write') && m.id !== me?.userId ? (
                  <select
                    value={m.roleKey}
                    aria-label={`Papel de ${m.name}`}
                    onChange={async (e) => {
                      await post(`/api/team/${m.id}/role`, { role: e.target.value });
                      team.reload();
                      toast('Papel atualizado.');
                    }}
                  >
                    {Object.entries(PAPEL).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                ) : (
                  (PAPEL[m.roleKey] ?? m.roleKey)
                ),
            },
            {
              chave: 'situacao',
              rotulo: 'Situação',
              render: (m) => (
                <Badge tone={m.status === 'active' ? 'ok' : 'critical'}>
                  {m.status === 'active' ? 'ativo' : 'suspenso'}
                </Badge>
              ),
            },
            {
              chave: 'acoes',
              rotulo: '',
              tipo: 'actions',
              render: (m) =>
                can('users.write') && m.id !== me?.userId ? (
                  <button
                    type="button"
                    className="small"
                    onClick={async () => {
                      await post(`/api/team/${m.id}/status`, {
                        status: m.status === 'active' ? 'suspended' : 'active',
                      });
                      team.reload();
                      toast('Situação atualizada.');
                    }}
                  >
                    {m.status === 'active' ? 'Suspender' : 'Reativar'}
                  </button>
                ) : null,
            },
          ]}
        />
      </Card>

      {novo ? (
        <NovoUsuario
          onClose={() => setNovo(false)}
          onCreated={() => {
            setNovo(false);
            team.reload();
            toast('Usuário criado.');
          }}
        />
      ) : null}
    </>
  );
}

function NovoUsuario({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('attendant');

  const { submit, saving, error } = useSubmit(async () => {
    await post('/api/team', { name, email, password, role });
    onCreated();
  });

  return (
    <Modal
      title="Novo usuário"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-usuario" className="primary" disabled={saving}>
            {saving ? 'Criando…' : 'Criar'}
          </button>
        </>
      }
    >
      <form id="form-usuario" onSubmit={submit}>
        <ErrorBox error={error} />
        <Field label="Nome" required>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="E-mail" required>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>
        <Field label="Senha provisória" required hint="Mínimo de 10 caracteres.">
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={10}
          />
        </Field>
        <Field label="Papel">
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {Object.entries(PAPEL).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------- WhatsApp

interface ChannelStatus {
  provider: string;
  connected: boolean;
  detail: string;
  paused: boolean;
  pausedReason: string | null;
  pausedAt: string | null;
  queued: number;
  deliveryUnknown: number;
  pendingConfirmations: { item: string; onde: string }[];
  webhookPath: string;
}

interface EntregaIncerta {
  id: string;
  body: string;
  last_error: string | null;
  updated_at: string;
  customer_name: string | null;
}

export function WhatsAppConfig() {
  const { can, store } = useSession();
  const toast = useToast();
  const status = useResource<ChannelStatus>('/api/channel/status');
  const incertas = useResource<{ items: EntregaIncerta[] }>('/api/channel/delivery-unknown');
  const [motivo, setMotivo] = useState('');

  const alternar = useSubmit(async () => {
    const rota = status.data?.paused ? '/api/channel/resume' : '/api/channel/pause';
    await post(rota, { reason: motivo });
    setMotivo('');
    status.reload();
    toast(status.data?.paused ? 'Envios religados.' : 'Envios pausados.');
  });

  const endereco = store ? `/api/webhooks/whatsapp/${store.companyId ? '' : ''}` : '';
  void endereco;

  return (
    <>
      <Card title="Conexão">
        <ErrorBox error={status.error} />
        {status.data?.provider === 'none' ? (
          <Alert tone="info" title="Nenhum conector ligado nesta instalação">
            O conector escolhido é o WAHA na própria VPS. Defina <code>WHATSAPP_PROVIDER=waha</code>,
            o endereço e a chave no arquivo <code>.env</code>, e reinicie os serviços.
          </Alert>
        ) : (
          <>
            <p>
              Conector: <strong>{status.data?.provider}</strong>{' '}
              <Badge tone={status.data?.connected ? 'ok' : 'critical'}>
                {status.data?.connected ? 'conectado' : 'fora do ar'}
              </Badge>
            </p>
            <p className="small muted">{status.data?.detail}</p>
          </>
        )}

        <div className="grid cols-3" style={{ marginTop: 12 }}>
          <div className="indicator">
            <div className="value">{status.data?.queued ?? 0}</div>
            <div className="label">Na fila de envio</div>
          </div>
          <div className="indicator">
            <div className="value">{status.data?.deliveryUnknown ?? 0}</div>
            <div className="label">Entrega incerta</div>
          </div>
          <div className="indicator">
            <div className="value">{status.data?.paused ? 'Sim' : 'Não'}</div>
            <div className="label">Envios pausados</div>
          </div>
        </div>
      </Card>

      <Card title="Endereço do webhook">
        <p className="small muted">
          Configure este endereço no WAHA para ele avisar o sistema a cada mensagem. A proteção é a
          assinatura configurada em <code>WAHA_WEBHOOK_SECRET</code> — endereço difícil de adivinhar,
          sozinho, não é proteção.
        </p>
        <div className="preview-box">{status.data?.webhookPath}</div>
      </Card>

      {status.data?.pendingConfirmations?.length ? (
        <Card
          title="Itens do conector ainda por confirmar"
          description="Pendência P02: conferir contra a documentação da versão do WAHA que vai rodar."
          tight
        >
          <Tabela
            linhas={status.data.pendingConfirmations}
            chave={(p) => p.item}
            vazio="Nada pendente."
            colunas={[
              { chave: 'item', rotulo: 'Item', tipo: 'principal', render: (p) => p.item },
              { chave: 'onde', rotulo: 'Onde conferir', render: (p) => p.onde },
            ]}
          />
        </Card>
      ) : null}

      <Card title="Contingência do canal">
        <ol style={{ paddingLeft: 20 }}>
          <li>Nunca conectar o mesmo número a dois motores ao mesmo tempo.</li>
          <li>Os disparos param sozinhos quando o envio falha: etapa com falha não avança.</li>
          <li>Se a queda passar de algumas horas, pause os envios aqui, com o motivo.</li>
          <li>A fila continua visível, com validade por mensagem.</li>
          <li>Religar exige decisão registrada. Etapas vencidas são descartadas, sem rajada.</li>
        </ol>

        {can('whatsapp.config.write') ? (
          <form onSubmit={alternar.submit}>
            <ErrorBox error={alternar.error} />
            {status.data?.paused ? (
              <Alert tone="warning" title="Envios pausados">
                Motivo registrado: {status.data.pausedReason ?? '—'}
              </Alert>
            ) : null}
            <Field label="Motivo" required hint="Fica registrado na auditoria.">
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                required
                minLength={3}
              />
            </Field>
            <button type="submit" disabled={alternar.saving || motivo.trim().length < 3}>
              {status.data?.paused ? 'Religar envios' : 'Pausar envios'}
            </button>
          </form>
        ) : null}
      </Card>

      <Card
        title="Entregas incertas"
        description="O provedor não confirmou. Nada é reenviado sozinho: alguém decide."
        tight
      >
        <ErrorBox error={incertas.error} />
        <Tabela
          linhas={incertas.data?.items ?? []}
          chave={(i) => i.id}
          vazio="Nenhuma entrega incerta."
          colunas={[
            {
              chave: 'cliente',
              rotulo: 'Cliente',
              tipo: 'principal',
              render: (i) => i.customer_name ?? 'sem cadastro',
            },
            { chave: 'quando', rotulo: 'Quando', render: (i) => formatDateTime(i.updated_at) },
            { chave: 'erro', rotulo: 'Motivo', render: (i) => <span className="small">{i.last_error}</span> },
            {
              chave: 'acoes',
              rotulo: '',
              tipo: 'actions',
              render: (i) =>
                can('whatsapp.config.write') ? (
                  <>
                    <button
                      type="button"
                      className="small"
                      onClick={async () => {
                        await post(`/api/channel/delivery-unknown/${i.id}/resolve`, {
                          decision: 'chegou',
                          reason: 'confirmado com o cliente',
                        });
                        incertas.reload();
                        status.reload();
                        toast('Marcada como entregue.');
                      }}
                    >
                      Chegou
                    </button>
                    <button
                      type="button"
                      className="small danger"
                      onClick={async () => {
                        await post(`/api/channel/delivery-unknown/${i.id}/resolve`, {
                          decision: 'nao_chegou',
                          reason: 'confirmado com o cliente',
                        });
                        incertas.reload();
                        status.reload();
                        toast('Marcada como não entregue.');
                      }}
                    >
                      Não chegou
                    </button>
                  </>
                ) : null,
            },
          ]}
        />
      </Card>
    </>
  );
}

// ------------------------------------------------------------ custos e limites

export function CustosELimites() {
  const { can } = useSession();
  const custos = useResource<{
    aiMonthlyBudgetCents: number;
    aiSpentCents: number;
    aiCalls: number;
    aiEnabled: boolean;
    retention: Record<string, number>;
  }>(can('costs.read') ? '/api/costs' : null);

  if (!can('costs.read')) {
    return (
      <Card>
        <Empty title="Você não tem permissão para ver custos." />
      </Card>
    );
  }

  return (
    <>
      <Card title="Consumo de IA">
        <ErrorBox error={custos.error} />
        {custos.data?.aiEnabled === false ? (
          <Alert tone="info" title="Assistente desligado">
            O teto mensal está configurado e passa a valer quando o assistente for ligado em{' '}
            Configurações › Regras da IA.
          </Alert>
        ) : null}
        <div className="grid cols-3">
          <div className="indicator">
            <div className="value">{formatBRL(custos.data?.aiMonthlyBudgetCents ?? 0)}</div>
            <div className="label">Teto mensal de IA</div>
            <div className="note">Ao atingir o limite, o sistema mantém agenda, cadastro e lembretes.</div>
          </div>
          <div className="indicator">
            <div className="value">{formatBRL(custos.data?.aiSpentCents ?? 0)}</div>
            <div className="label">Gasto no mês</div>
            <div className="note">Soma do uso já liquidado.</div>
          </div>
          <div className="indicator">
            <div className="value">{custos.data?.aiCalls ?? 0}</div>
            <div className="label">Chamadas de IA</div>
            <div className="note">Contagem do mês corrente.</div>
          </div>
        </div>
      </Card>

      <Card title="Retenção de dados" description="Prazos atuais. A aprovação e o expurgo ficam em Configurações › Retenção de dados.">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Dado</th>
                <th className="num">Retenção</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Mensagens', `${custos.data?.retention?.messagesMonths ?? 24} meses`],
                ['Áudios originais', `${custos.data?.retention?.audioDays ?? 90} dias`],
                ['Transcrições', `${custos.data?.retention?.transcriptsMonths ?? 24} meses`],
                ['Logs de aplicação', `${custos.data?.retention?.appLogsDays ?? 30} dias`],
                ['Auditoria', `${custos.data?.retention?.auditMonths ?? 60} meses`],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td className="num">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
